import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowUp, ArrowDown, ArrowUpDown,
  TrendingUp, TrendingDown, Brain,
  BarChart3, PieChart, Building2, Calendar,
  DollarSign, ExternalLink, Eye, FileText,
  Scale, Landmark, Globe, Users, Target,
  ChevronLeft, ChevronRight, Briefcase,
  Megaphone, Gavel, ScrollText
} from 'lucide-react';
import './LobbyingDetailPage.css';

// =============================================================================
// MOCK DATA — keyed by ticker
// =============================================================================

const lobbyingDetailData = {
  'AAPL': {
    ticker: 'AAPL', company: 'Apple Inc.', sector: 'Technology',
    headquarters: 'Cupertino, CA', employees: 164000,
    totalSpend2025: 38400000, totalSpend2024: 34200000,
    totalSpendAllTime: 142000000, activeRegistrants: 8,
    lobbyingRank: 14, sectorRank: 3,
    summary: 'Apple has steadily increased lobbying spend since 2019, focusing on taxation, digital services regulation, privacy legislation, and antitrust. The company deploys 8 registered lobbying firms across Washington, with Fierce Government Relations as their primary registrant.',
    quarterlySpend: [
      { quarter: 'Q4 2025', amount: 9870000 },
      { quarter: 'Q3 2025', amount: 10200000 },
      { quarter: 'Q2 2025', amount: 9450000 },
      { quarter: 'Q1 2025', amount: 8880000 },
      { quarter: 'Q4 2024', amount: 9100000 },
      { quarter: 'Q3 2024', amount: 8800000 },
      { quarter: 'Q2 2024', amount: 8400000 },
      { quarter: 'Q1 2024', amount: 7900000 },
    ],
    annualSpend: [
      { year: 2025, amount: 38400000 },
      { year: 2024, amount: 34200000 },
      { year: 2023, amount: 29800000 },
      { year: 2022, amount: 25600000 },
      { year: 2021, amount: 20400000 },
      { year: 2020, amount: 16800000 },
    ],
    issueBreakdown: [
      { issue: 'Taxation', weight: 28, amount: 10750000, description: 'Corporate tax reform, global minimum tax, digital services taxes in EU and Asia' },
      { issue: 'Science/Technology', weight: 22, amount: 8450000, description: 'AI regulation framework, encryption policy, app store legislation, right-to-repair' },
      { issue: 'Trade', weight: 18, amount: 6910000, description: 'China tariff exemptions, supply chain diversification incentives, IP protections' },
      { issue: 'Telecommunications', weight: 15, amount: 5760000, description: 'Spectrum allocation, satellite communications regulation, 6G research funding' },
      { issue: 'Copyright/Patent', weight: 10, amount: 3840000, description: 'Patent troll reform, IP licensing standards, DMCA modernization' },
      { issue: 'Civil Rights', weight: 7, amount: 2690000, description: 'Data privacy legislation (state & federal), CCPA/CPRA implementation, child safety' },
    ],
    registrants: [
      { name: 'Fierce Government Relations', amount: 2680000, lobbyists: 6, focus: 'Taxation, Trade, IP', active: true },
      { name: 'Franklin Square Group', amount: 1920000, lobbyists: 4, focus: 'Technology, Telecom', active: true },
      { name: 'Invariant LLC', amount: 1540000, lobbyists: 3, focus: 'AI Regulation, Privacy', active: true },
      { name: 'Cassidy & Associates', amount: 1280000, lobbyists: 4, focus: 'Trade, Supply Chain', active: true },
      { name: 'Capitol Counsel', amount: 980000, lobbyists: 2, focus: 'Copyright, Patent Reform', active: true },
      { name: 'McGuireWoods Consulting', amount: 720000, lobbyists: 2, focus: 'State Lobbying, Privacy', active: true },
      { name: 'Brownstein Hyatt Farber Schreck', amount: 450000, lobbyists: 2, focus: 'Antitrust', active: false },
      { name: 'Holland & Knight', amount: 300000, lobbyists: 1, focus: 'Government Contracts', active: false },
    ],
    specificBills: [
      { bill: 'S. 1578', title: 'American Innovation and Choice Online Act', status: 'Committee', stance: 'Oppose', issue: 'Antitrust', chamber: 'Senate', description: 'Would prohibit dominant platforms from self-preferencing their own products — directly impacts App Store policies.' },
      { bill: 'H.R. 3816', title: 'Open App Markets Act', status: 'Committee', stance: 'Oppose', issue: 'Competition', chamber: 'House', description: 'Requires app stores to allow sideloading and third-party payment systems, threatening Apple\'s 30% commission model.' },
      { bill: 'S. 2992', title: 'AI Accountability Act', status: 'Floor Vote', stance: 'Support', issue: 'AI Regulation', chamber: 'Senate', description: 'Establishes framework for AI safety testing — Apple supports measured regulation that doesn\'t stifle Siri and ML development.' },
      { bill: 'H.R. 4120', title: 'Global Corporate Minimum Tax Act', status: 'Committee', stance: 'Neutral', issue: 'Taxation', chamber: 'House', description: 'Implements OECD Pillar Two 15% global minimum tax. Apple has publicly supported the framework while lobbying for specific exemptions.' },
      { bill: 'S. 3456', title: 'ENCRYPT Act', status: 'Introduced', stance: 'Support', issue: 'Privacy', chamber: 'Senate', description: 'Prevents states from mandating encryption backdoors — directly protects Apple\'s end-to-end encryption in iMessage and iCloud.' },
      { bill: 'H.R. 2178', title: 'Right to Repair Act', status: 'Committee', stance: 'Oppose', issue: 'Consumer Rights', chamber: 'House', description: 'Mandates manufacturers provide repair tools and parts to consumers. Apple opposes citing security and IP concerns.' },
    ],
    relatedCongressional: [
      { politician: 'Nancy Pelosi', party: 'D', chamber: 'House', action: 'Purchased AAPL calls', amount: '$500K - $1M', date: '2026-01-16', relevance: 'Sits on committee overseeing tech regulation' },
      { politician: 'Dan Crenshaw', party: 'R', chamber: 'House', action: 'Purchased AAPL shares', amount: '$15K - $50K', date: '2025-12-20', relevance: 'Member of House Technology Caucus' },
      { politician: 'Tommy Tuberville', party: 'R', chamber: 'Senate', action: 'Sold AAPL shares', amount: '$50K - $100K', date: '2025-11-15', relevance: 'Member of Senate Armed Services Committee' },
    ],
    aiInsight: "Apple's lobbying strategy reveals a defensive posture on antitrust (opposing the Open App Markets Act and American Innovation and Choice Online Act) while proactively supporting encryption protections. The 12.3% YoY spend increase is driven by two emerging fronts: AI regulation (where Apple wants to shape rules before they crystallize) and global tax reform (where the OECD minimum tax directly impacts Apple's international tax structure). Notable: Apple's top lobbying firm, Fierce Government Relations, added 2 lobbyists in Q4 2025, signaling escalation. The overlap between Apple's lobbying targets and congressional trading activity (Pelosi's AAPL options purchases during active App Store legislation) warrants monitoring.",
  },
  'AMZN': {
    ticker: 'AMZN', company: 'Amazon.com Services LLC', sector: 'Consumer Discretionary',
    headquarters: 'Seattle, WA', employees: 1540000,
    totalSpend2025: 84200000, totalSpend2024: 74800000,
    totalSpendAllTime: 312000000, activeRegistrants: 14,
    lobbyingRank: 2, sectorRank: 1,
    summary: 'Amazon is the second-largest corporate lobbyist in the US, deploying 14 registered firms with $84.2M in 2025 spend. Focus areas span antitrust defense, labor regulation, drone delivery, AWS government cloud contracts, and trade policy.',
    quarterlySpend: [
      { quarter: 'Q4 2025', amount: 21380000 },
      { quarter: 'Q3 2025', amount: 22100000 },
      { quarter: 'Q2 2025', amount: 20800000 },
      { quarter: 'Q1 2025', amount: 19920000 },
      { quarter: 'Q4 2024', amount: 19500000 },
      { quarter: 'Q3 2024', amount: 18900000 },
      { quarter: 'Q2 2024', amount: 18400000 },
      { quarter: 'Q1 2024', amount: 18000000 },
    ],
    annualSpend: [
      { year: 2025, amount: 84200000 },
      { year: 2024, amount: 74800000 },
      { year: 2023, amount: 68400000 },
      { year: 2022, amount: 58200000 },
      { year: 2021, amount: 42600000 },
      { year: 2020, amount: 32800000 },
    ],
    issueBreakdown: [
      { issue: 'Trade/Antitrust', weight: 30, amount: 25260000, description: 'Antitrust legislation, marketplace competition rules, seller fee regulations' },
      { issue: 'Labor', weight: 20, amount: 16840000, description: 'Warehouse worker regulations, unionization response, gig economy classification' },
      { issue: 'Defense/Cloud', weight: 18, amount: 15160000, description: 'AWS GovCloud contracts, JEDI/JWCC procurement, FedRAMP authorization' },
      { issue: 'Transportation', weight: 14, amount: 11790000, description: 'Drone delivery FAA regulations, autonomous vehicle policy, last-mile logistics' },
      { issue: 'Taxation', weight: 10, amount: 8420000, description: 'Online sales tax collection, digital services taxes, transfer pricing' },
      { issue: 'Environment', weight: 8, amount: 6730000, description: 'Carbon reporting standards, EV delivery fleet incentives, packaging regulations' },
    ],
    registrants: [
      { name: 'Akin Gump Strauss Hauer & Feld', amount: 4200000, lobbyists: 8, focus: 'Antitrust, Trade', active: true },
      { name: 'Covington & Burling', amount: 3800000, lobbyists: 6, focus: 'Defense, Cloud', active: true },
      { name: 'Davis Polk & Wardwell', amount: 2900000, lobbyists: 5, focus: 'Labor, Regulation', active: true },
      { name: 'King & Spalding', amount: 2400000, lobbyists: 4, focus: 'Taxation, Trade', active: true },
      { name: 'Arnold & Porter', amount: 2100000, lobbyists: 3, focus: 'Transportation, Drones', active: true },
    ],
    specificBills: [
      { bill: 'S. 2710', title: 'Warehouse Worker Protection Act', status: 'Committee', stance: 'Oppose', issue: 'Labor', chamber: 'Senate', description: 'Mandates ergonomic standards, heat protections, and pace-of-work disclosures for warehouse workers.' },
      { bill: 'H.R. 3816', title: 'Open App Markets Act', status: 'Committee', stance: 'Support', issue: 'Competition', chamber: 'House', description: 'Amazon supports this bill as it would weaken Apple/Google app store dominance, benefiting Appstore competitors.' },
      { bill: 'S. 1578', title: 'American Innovation and Choice Online Act', status: 'Committee', stance: 'Oppose', issue: 'Antitrust', chamber: 'Senate', description: 'Would limit Amazon from preferencing its own private label products in search results.' },
    ],
    relatedCongressional: [
      { politician: 'Mark Kelly', party: 'D', chamber: 'Senate', action: 'Purchased AMZN shares', amount: '$100K - $250K', date: '2026-01-20', relevance: 'Member of Senate Commerce Committee' },
    ],
    aiInsight: "Amazon's lobbying machine is second only to Meta in tech. The $84.2M annual spend reveals a company fighting on every front: antitrust defense ($25.3M), labor regulation opposition ($16.8M), and aggressive pursuit of government cloud contracts ($15.2M). The drone delivery push is notable — Amazon has 5 lobbyists dedicated solely to FAA regulations for Prime Air. Interestingly, Amazon supports the Open App Markets Act that Apple opposes, highlighting how tech giants weaponize regulation against each other.",
  },
};

// Fallback generator
const generateDefault = (ticker) => ({
  ticker, company: `${ticker} Corporation`, sector: 'Unknown',
  headquarters: 'N/A', employees: 0,
  totalSpend2025: 5000000, totalSpend2024: 4500000,
  totalSpendAllTime: 25000000, activeRegistrants: 3,
  lobbyingRank: 50, sectorRank: 10,
  summary: `${ticker} is a publicly traded company with active lobbying operations in Washington, D.C.`,
  quarterlySpend: [
    { quarter: 'Q4 2025', amount: 1250000 },
    { quarter: 'Q3 2025', amount: 1300000 },
    { quarter: 'Q2 2025', amount: 1200000 },
    { quarter: 'Q1 2025', amount: 1250000 },
  ],
  annualSpend: [
    { year: 2025, amount: 5000000 },
    { year: 2024, amount: 4500000 },
    { year: 2023, amount: 4100000 },
  ],
  issueBreakdown: [
    { issue: 'General', weight: 50, amount: 2500000, description: 'General corporate lobbying on regulatory issues' },
    { issue: 'Taxation', weight: 30, amount: 1500000, description: 'Tax policy and compliance' },
    { issue: 'Trade', weight: 20, amount: 1000000, description: 'International trade regulations' },
  ],
  registrants: [
    { name: 'General Lobbying Firm', amount: 1250000, lobbyists: 2, focus: 'General', active: true },
  ],
  specificBills: [],
  relatedCongressional: [],
  aiInsight: `${ticker}'s lobbying activity is moderate relative to its sector peers. Limited public data is available on specific legislative targets.`,
});

// =============================================================================
// HELPERS
// =============================================================================
const formatCurrency = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function LobbyingDetailPage() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const sym = (ticker || '').toUpperCase();
  const data = lobbyingDetailData[sym] || generateDefault(sym);

  const yoyChange = ((data.totalSpend2025 - data.totalSpend2024) / data.totalSpend2024 * 100).toFixed(1);
  const maxQuarterly = Math.max(...data.quarterlySpend.map(q => q.amount));
  const maxAnnual = Math.max(...data.annualSpend.map(a => a.amount));
  const maxIssue = Math.max(...data.issueBreakdown.map(i => i.weight));

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'bills', label: `Bills & Legislation (${data.specificBills.length})` },
    { id: 'registrants', label: `Registrants (${data.registrants.length})` },
  ];

  const stanceColor = (stance) => {
    if (stance === 'Support') return 'positive';
    if (stance === 'Oppose') return 'negative';
    return 'neutral';
  };

  return (
    <div className="lobbying-detail-page">
      {/* Back Header */}
      <div className="ld-header">
        <button className="ld-back-btn" onClick={() => navigate('/datasets/lobbying')}>
          <ArrowLeft size={18} /> Lobbying Data
        </button>
      </div>

      {/* Company Header */}
      <div className="ld-company-header">
        <div className="ld-company-left">
          <div className="ld-company-icon"><Scale size={24} /></div>
          <div>
            <div className="ld-company-top">
              <h1>{data.company}</h1>
              <span className="ld-ticker-badge">{data.ticker}</span>
            </div>
            <p className="ld-company-meta">{data.sector} • {data.headquarters} • {data.employees.toLocaleString()} employees</p>
          </div>
        </div>
        <button className="ld-stock-btn" onClick={() => navigate(`/stock/${data.ticker}?tab=government`)}>
          <Eye size={14} /> View Stock
        </button>
      </div>

      {/* Stats Row */}
      <div className="ld-stats-row">
        <div className="ld-stat">
          <div className="ld-stat-icon purple"><DollarSign size={18} /></div>
          <span className="ld-stat-label">2025 Total Spend</span><span className="ld-stat-value">{formatCurrency(data.totalSpend2025)}</span><span className={`ld-stat-change ${yoyChange >= 0 ? 'positive' : 'negative'}`}>{yoyChange >= 0 ? '+' : ''}{yoyChange}% YoY</span>
        </div>
        <div className="ld-stat">
          <div className="ld-stat-icon green"><TrendingUp size={18} /></div>
          <span className="ld-stat-label">All-Time Spend</span><span className="ld-stat-value">{formatCurrency(data.totalSpendAllTime)}</span><span className="ld-stat-sub">Since tracking began</span>
        </div>
        <div className="ld-stat">
          <div className="ld-stat-icon amber"><Users size={18} /></div>
          <span className="ld-stat-label">Active Registrants</span><span className="ld-stat-value">{data.activeRegistrants}</span><span className="ld-stat-sub">Lobbying firms</span>
        </div>
        <div className="ld-stat">
          <div className="ld-stat-icon red"><Target size={18} /></div>
          <span className="ld-stat-label">Overall Rank</span><span className="ld-stat-value">#{data.lobbyingRank}</span><span className="ld-stat-sub">#{data.sectorRank} in {data.sector}</span>
        </div>
      </div>

      {/* AI Insight */}
      <div className="ld-ai-card">
        <div className="ld-ai-badge"><Brain size={14} /> AI Insight</div>
        <p>{data.aiInsight}</p>
      </div>

      {/* Tabs */}
      <div className="ld-tabs">
        {tabs.map(tab => (
          <button key={tab.id} className={`ld-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>{tab.label}</button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="ld-tab-content">
          <div className="ld-grid-2col">
            {/* Issue Breakdown */}
            <div className="ld-panel">
              <h3 className="ld-panel-title"><PieChart size={16} /> What They Lobby For</h3>
              <p className="ld-panel-desc">{data.summary}</p>
              <div className="ld-issue-list">
                {data.issueBreakdown.map((item, idx) => (
                  <div key={idx} className="ld-issue-item">
                    <div className="ld-issue-header">
                      <span className="ld-issue-name">{item.issue}</span>
                      <span className="ld-issue-pct">{item.weight}% • {formatCurrency(item.amount)}</span>
                    </div>
                    <div className="ld-issue-bar-bg"><div className="ld-issue-bar" style={{ width: `${(item.weight / maxIssue) * 100}%` }}></div></div>
                    <p className="ld-issue-desc">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quarterly Spend */}
            <div className="ld-panel">
              <h3 className="ld-panel-title"><BarChart3 size={16} /> Quarterly Spending</h3>
              <div className="ld-bar-chart">
                {data.quarterlySpend.map((q, idx) => (
                  <div key={idx} className="ld-bar-row">
                    <span className="ld-bar-label">{q.quarter}</span>
                    <div className="ld-bar-track"><div className="ld-bar-fill" style={{ width: `${(q.amount / maxQuarterly) * 100}%` }}></div></div>
                    <span className="ld-bar-value">{formatCurrency(q.amount)}</span>
                  </div>
                ))}
              </div>

              <h3 className="ld-panel-title" style={{ marginTop: 24 }}><Calendar size={16} /> Annual Trend</h3>
              <div className="ld-bar-chart">
                {data.annualSpend.map((a, idx) => (
                  <div key={idx} className="ld-bar-row">
                    <span className="ld-bar-label">{a.year}</span>
                    <div className="ld-bar-track"><div className="ld-bar-fill annual" style={{ width: `${(a.amount / maxAnnual) * 100}%` }}></div></div>
                    <span className="ld-bar-value">{formatCurrency(a.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Congressional Overlap */}
          {data.relatedCongressional.length > 0 && (
            <div className="ld-panel" style={{ marginTop: 16 }}>
              <h3 className="ld-panel-title"><Landmark size={16} /> Congressional Trading Overlap</h3>
              <p className="ld-panel-desc-sm">Members of Congress who traded {data.ticker} while related legislation was active.</p>
              <div className="ld-congress-list">
                {data.relatedCongressional.map((c, idx) => (
                  <div key={idx} className="ld-congress-row" onClick={() => navigate(`/politician/${c.politician.toLowerCase().replace(/\s/g, '-')}`)}>
                    <div className="ld-congress-info">
                      <span className="ld-congress-name">{c.politician}</span>
                      <span className={`ld-congress-party ${c.party === 'D' ? 'dem' : 'rep'}`}>{c.party === 'D' ? 'Democrat' : 'Republican'} • {c.chamber}</span>
                    </div>
                    <div className="ld-congress-action">
                      <span className={c.action.includes('Purchased') ? 'positive' : 'negative'}>{c.action}</span>
                      <span className="ld-congress-amount">{c.amount}</span>
                    </div>
                    <div className="ld-congress-relevance">{c.relevance}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'bills' && (
        <div className="ld-tab-content">
          <div className="ld-bills-list">
            {data.specificBills.length === 0 ? (
              <div className="ld-empty"><ScrollText size={32} /><p>No specific bills tracked for {data.ticker}</p></div>
            ) : data.specificBills.map((bill, idx) => (
              <div key={idx} className="ld-bill-card">
                <div className="ld-bill-top">
                  <div className="ld-bill-id">
                    <span className="ld-bill-number">{bill.bill}</span>
                    <span className={`ld-bill-chamber ${bill.chamber === 'Senate' ? 'senate' : 'house'}`}>{bill.chamber}</span>
                  </div>
                  <div className="ld-bill-tags">
                    <span className={`ld-stance-badge ${stanceColor(bill.stance)}`}>{bill.stance}</span>
                    <span className="ld-status-badge">{bill.status}</span>
                  </div>
                </div>
                <h4 className="ld-bill-title">{bill.title}</h4>
                <span className="ld-bill-issue">{bill.issue}</span>
                <p className="ld-bill-desc">{bill.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'registrants' && (
        <div className="ld-tab-content">
          <div className="ld-registrants-list">
            {data.registrants.map((reg, idx) => (
              <div key={idx} className="ld-registrant-card">
                <div className="ld-reg-top">
                  <div>
                    <h4 className="ld-reg-name">{reg.name}</h4>
                    <span className="ld-reg-focus">{reg.focus}</span>
                  </div>
                  <div className="ld-reg-stats">
                    <span className="ld-reg-amount">{formatCurrency(reg.amount)}</span>
                    <span className={`ld-reg-status ${reg.active ? 'active' : 'inactive'}`}>{reg.active ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
                <div className="ld-reg-bottom">
                  <span><Users size={12} /> {reg.lobbyists} registered lobbyists</span>
                  <span className="ld-reg-pct">{((reg.amount / data.quarterlySpend[0].amount) * 100).toFixed(0)}% of Q4 spend</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}